/**
 * MedicationsChartTab — Medications inner tab of the patient Chart shell
 * (PROD-737 PR A, read-only). Add / Edit / Stop dialogs + SignalR meds
 * sync ship in subsequent PRs (B and C).
 *
 * Flutter ref: `patient_chart/.../tab_widgets/meds_tab.dart`. Partitions
 * by `IsActive` — Active section at top, Past below.
 *
 * FHIR: `MedicationStatement` R4 when `isOrder=false`,
 * `MedicationRequest` R4 when `isOrder=true` (Rx).
 */

import { ChartEmpty } from './chart-empty';
import { Skeleton } from '@scripsteam/scrips-signal-ds';
import { usePatientMedications } from '@/patients/services/use-patient-medications';
import { ChartSectionHeader } from './chart-section-header';
import { useReportChartCount } from './chart-page-context';
import { ChartShowAll } from './chart-row-cap';
import { useRowCap } from './use-row-cap';
import type { PatientMedication } from '@/types/patient-chart-api';
import { V2_BODY_EMPHASIS, v2Look } from '@/shared/ui/design-v2';
import { CHART_TD_NOWRAP_V2, CHART_TD_V2, CHART_TD_WIDE_V2, CHART_TH_V2, chartCell, chartTableCard, type ChartCell } from './chart-table-v2';
import { EmptyState } from '@scripsteam/scrips-signal-ds';
import { clinicalDisplayName } from '@/lib/clinical-display-name';

export interface MedicationsChartTabProps {
  patientId: string | null | undefined;
}

export function MedicationsChartTab({ patientId }: MedicationsChartTabProps) {
  const activeQ = usePatientMedications(patientId, true);
  const pastQ = usePatientMedications(patientId, false);

  const active = (activeQ.data ?? []).filter((m) => m.isDeleted !== true);
  const past = (pastQ.data ?? []).filter((m) => m.isDeleted !== true);
  // The index's count: every medication on file, active and past (nothing while either list loads).
  useReportChartCount(activeQ.isLoading || pastQ.isLoading ? null : active.length + past.length);

  if (!patientId) {
    return (
      <div className="p-6">
        <EmptyState
            icon="info"
            title="Save the patient first to record medications."
          />
      </div>
    );
  }

  const isLoading = activeQ.isLoading || pastQ.isLoading;
  const isError = activeQ.isError || pastQ.isError;

  if (isLoading) {
    return (
      <div className="p-6 space-y-3">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="p-6">
        <EmptyState
            icon="warning"
            title="Couldn't load medications."
            ctaLabel="Retry"
            onCtaClick={() => { void activeQ.refetch(); void pastQ.refetch(); }}
          />
      </div>
    );
  }

  if (active.length === 0 && past.length === 0) {
    return (
      <div className="p-6">
        <EmptyState
            icon="pill"
            title="No medications recorded for this patient."
          />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <Section
        title="Active medications"
        rows={active}
        emptyMessage="No active medications."
        showEnd={false}
      />
      <Section
        title="Past medications"
        rows={past}
        emptyMessage="No past medications on file."
        showEnd={true}
      />
    </div>
  );
}

function Section({
  title,
  rows,
  emptyMessage,
  showEnd,
}: {
  title: string;
  rows: PatientMedication[];
  emptyMessage: string;
  showEnd: boolean;
}) {
  const cap = useRowCap(rows);
  return (
    <div>
      {/* Medications are added via the encounter Rx authoring flow only —
       *  there's no chart-level "+" affordance. Hide the add icon. */}
      <ChartSectionHeader title={title} count={rows.length} showAdd={false} />
      {rows.length === 0 ? (
        <ChartEmpty title={emptyMessage} />
      ) : (
        <div className={chartTableCard('rounded-lg border border-border-default overflow-hidden bg-surface-card')}>
          <table className="w-full text-sm">
            <thead className="bg-grey-100">
              <tr className="text-left text-text-muted">
                <Th>#</Th>
                <Th>Medication</Th>
                <Th>Type</Th>
                <Th>Form</Th>
                <Th>Route</Th>
                <Th>Dose</Th>
                <Th>Frequency</Th>
                <Th>Started</Th>
                {showEnd ? <Th>Stopped</Th> : null}
                <Th>Note</Th>
              </tr>
            </thead>
            <tbody>
              {cap.visible.map((m, i) => (
                <tr
                  key={m.identifier ?? `${i}-${m.code?.code ?? 'unknown'}`}
                  className="border-t border-border-default hover:bg-grey-100/50"
                >
                  <Td fit="nowrap" cell="index">{i + 1}</Td>
                  <Td fit="wide" cell="name">
                    <span className={v2Look('font-medium text-brand-depth', V2_BODY_EMPHASIS)}>
                      {clinicalDisplayName(m.code).name || '—'}
                    </span>
                    {m.code?.code ? (
                      <div className="text-xs text-text-hint">
                        {m.code.code}
                      </div>
                    ) : null}
                  </Td>
                  <Td fit="nowrap" cell="short">
                    {/* Rx vs administered-only — Flutter parity. Plain text: a kind, not a status (it was a
                        blue pill on every row, a colour that carried nothing). */}
                    <span>{m.isOrder === true ? 'Rx' : 'Recorded'}</span>
                  </Td>
                  <Td cell="short">{m.form?.display ?? '—'}</Td>
                  <Td cell="short">{m.route?.display ?? '—'}</Td>
                  <Td fit="nowrap" cell="short">{formatDose(m)}</Td>
                  <Td cell="text">{formatFrequency(m)}</Td>
                  <Td fit="nowrap" date>
                    {formatDate(
                      m.administration?.startDate,
                      m.administration?.startDisplayDate,
                    )}
                  </Td>
                  {showEnd ? (
                    <Td fit="nowrap" date>
                      {formatDate(
                        m.administration?.endDate,
                        m.administration?.endDisplayDate,
                      )}
                    </Td>
                  ) : null}
                  <Td cell="note" tip={m.administration?.note?.trim() || '—'}>{m.administration?.note?.trim() || '—'}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ChartShowAll cap={cap} label={title.toLowerCase()} />
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className={chartCell('px-3 py-2 text-xs font-bold uppercase tracking-wide text-left', CHART_TH_V2)}>
      {children}
    </th>
  );
}

/** `fit` (switch ON only): `nowrap` keeps a short value on one line, `wide` gives the name room. */
function Td({
  children,
  fit,
  date = false,
  cell,
  tip,
}: {
  children: React.ReactNode;
  fit?: 'nowrap' | 'wide';
  date?: boolean;
  /** What the column holds (the bridge sizes it: index, name, text, short, date, note). */
  cell?: ChartCell;
  /** A note's full text for the app's instant tooltip, shown when the one-line cell is cut off. */
  tip?: string;
}) {
  const v2 = fit === 'nowrap' ? CHART_TD_NOWRAP_V2 : fit === 'wide' ? CHART_TD_WIDE_V2 : CHART_TD_V2;
  return (
    <td data-chart-cell={date ? 'date' : cell} data-tip={tip} className={chartCell('px-3 py-2 text-left align-top', v2)}>
      {children}
    </td>
  );
}

function formatDose(m: PatientMedication): string {
  const occ = m.occurenceRequest?.[0];
  if (!occ || (occ.count == null && occ.period == null)) return '—';
  // "1.5 unit × 3/day" — same shape as Flutter's compact dose string.
  const count = occ.count?.toString().trim() || '—';
  const unit = m.unitOfMeasurement?.trim() || '';
  const period = occ.period != null ? `${occ.period}/day` : '';
  const parts = [count + (unit ? ` ${unit}` : ''), period].filter(Boolean);
  return parts.join(' × ') || '—';
}

function formatFrequency(m: PatientMedication): string {
  const f = m.frequency?.trim();
  const d = m.duration?.trim();
  const dt = m.durationType?.trim();
  if (!f && !d) return '—';
  const dur = d ? `${d}${dt ? ` ${dt}` : ''}` : '';
  return [f, dur].filter(Boolean).join(' · ') || '—';
}

function formatDate(
  iso: string | null | undefined,
  displayFallback?: string | null,
): string {
  if (iso) {
    const d = new Date(iso);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
      });
    }
  }
  if (displayFallback?.trim()) return displayFallback;
  return '—';
}
